import { useState, useEffect, useRef } from 'react'
import { CreditCard, TrendingUp, Users, TrendingDown, Clock, Info } from 'lucide-react'
import { RevenueSkeleton } from '@/components/skeletons'
import { XAxis, YAxis, AreaChart, Area } from 'recharts'
import { Link } from 'react-router-dom'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from '@/components/ui/chart'
import { DashboardDateRangePicker } from '@/components/ui/dashboard-date-range-picker'


import { RevenueService, type RevenueStats, type PlanDistributionItem, type ChurnDataItem, type MrrTrendPoint, type TrialExpiryItem } from '@/services/revenue'
import { PlanDistributionCard } from './PlanDistributionCard'
import { useAuth } from '@/contexts/AuthContext'

// =========================================================================
// Chart configs
// =========================================================================

const mrrChartConfig = {
    mrr: { label: 'MRR', color: '#6366f1' },
} satisfies ChartConfig

// Reason label that shows a tooltip with the full text only when it's visually truncated
const TruncatedReason = ({ text }: { text: string }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
    }, [text])

    const label = (
        <span ref={ref} className="block truncate text-sm font-medium">
            {text}
        </span>
    )

    if (!isTruncated) return label

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>{label}</TooltipTrigger>
                <TooltipContent className="max-w-[280px] text-xs font-normal">{text}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

// =========================================================================
// KPI card definitions
// =========================================================================

const getStatsCards = (stats: RevenueStats | null) => [
    {
        title: 'MRR',
        value: stats ? `$${stats.mrr.toLocaleString()}` : '—',
        change: stats?.periodComparison?.mrrChange ?? '',
        icon: CreditCard,
        description: 'Monthly Recurring Revenue',
        iconBg: 'bg-indigo-100 dark:bg-indigo-950/50',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        tooltip: 'Sum of monthly plan prices for all currently active subscriptions.',

    },
    {
        title: 'ARR',
        value: stats ? `$${stats.arr.toLocaleString()}` : '—',
        change: stats?.periodComparison?.arrChange ?? '',
        icon: TrendingUp,
        description: 'Annual Run-Rate',
        iconBg: 'bg-emerald-100 dark:bg-emerald-950/50',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        tooltip: 'MRR × 12. Projected annual revenue based on current monthly rate.',

    },
    {
        title: 'Trial Conversion',
        value: stats ? `${stats.trialConversionRate}%` : '—',
        change: stats?.periodComparison?.trialConversionChange ?? '',
        icon: TrendingUp,
        description: 'Trials converted to paid',
        iconBg: 'bg-amber-100 dark:bg-amber-950/50',
        iconColor: 'text-amber-600 dark:text-amber-400',
        tooltip: 'Percentage of agencies that started a trial and converted to an active paid subscription.',

    },
    {
        title: 'Churn Rate',
        value: stats ? `${stats.churnRate}%` : '—',
        change: stats?.periodComparison?.churnChange ?? '',
        icon: TrendingDown,
        description: 'Cancelled in period',
        iconBg: 'bg-red-100 dark:bg-red-950/50',
        iconColor: 'text-red-600 dark:text-red-400',
        tooltip: 'Agencies cancelled in the selected period divided by active agencies at the start of the period.',
        invertTrend: true,

    },
    {
        title: 'ARPU',
        value: stats ? `$${stats.arpu.toLocaleString()}` : '—',
        change: stats?.periodComparison?.arpuChange ?? '',
        icon: Users,
        description: 'Avg Revenue Per User',
        iconBg: 'bg-violet-100 dark:bg-violet-950/50',
        iconColor: 'text-violet-600 dark:text-violet-400',
        tooltip: 'MRR divided by the number of active paid agencies.',

    },
    {
        title: 'LTV Estimate',
        value: stats?.ltvEstimate ? `$${stats.ltvEstimate.toLocaleString()}` : '—',
        change: '',
        icon: Clock,
        description: 'Lifetime Value',
        iconBg: 'bg-cyan-100 dark:bg-cyan-950/50',
        iconColor: 'text-cyan-600 dark:text-cyan-400',
        tooltip: 'ARPU divided by monthly churn rate. Estimated total revenue from an average customer over their lifetime.',
        filtered: false,
    },
]

// =========================================================================
// Revenue Page
// =========================================================================

const Revenue = () => {
    const { user } = useAuth()
    const [dateRange, setDateRange] = useState<string>('1m')
    const [isRangeLoading, setIsRangeLoading] = useState(true)
    const [isFixedLoading, setIsFixedLoading] = useState(true)

    const [stats, setStats] = useState<RevenueStats | null>(null)
    const [planDistribution, setPlanDistribution] = useState<PlanDistributionItem[]>([])
    const [churnData, setChurnData] = useState<ChurnDataItem[]>([])
    const [mrrTrend, setMrrTrend] = useState<MrrTrendPoint[]>([])
    const [trialExpiry, setTrialExpiry] = useState<TrialExpiryItem[]>([])



    // Stats, churn and the MRR trend are the only requests the date range changes.
    useEffect(() => {
        const loadRangeData = async () => {
            setIsRangeLoading(true)
            try {
                const [statsData, churnRes, mrrRes] = await Promise.all([
                    RevenueService.getStats(dateRange),
                    RevenueService.getChurnAnalytics(dateRange),
                    RevenueService.getMrrTrend(dateRange, user?.timezone),
                ])
                setStats(statsData)
                setChurnData(churnRes)
                setMrrTrend(mrrRes)
            } catch (error) {
                console.error('Failed to load revenue data:', error)
            } finally {
                setIsRangeLoading(false)
            }
        }
        loadRangeData()
    }, [dateRange])

    // Plan distribution and trial expiry take no range and return the same
    // payload whatever the pills say, so a range click must not refetch them.
    useEffect(() => {
        const loadFixedData = async () => {
            setIsFixedLoading(true)
            try {
                const [planData, trialRes] = await Promise.all([
                    RevenueService.getPlanDistribution(),
                    RevenueService.getTrialExpiry(),
                ])
                setPlanDistribution(planData)
                setTrialExpiry(trialRes)
            } catch (error) {
                console.error('Failed to load revenue data:', error)
            } finally {
                setIsFixedLoading(false)
            }
        }
        loadFixedData()
    }, [])

    // One skeleton for the whole page, as when all five loaded together.
    const isLoading = isRangeLoading || isFixedLoading

    const statsCards = getStatsCards(stats)

    // Cancellation reasons — separate the non-answer from the actual reasons.
    // "No reason provided" is a response-rate fact, not a reason, so it gets its own stat
    // instead of crushing the scale of the ranked list. churnData is sorted by count from the API.
    const totalCancels = churnData.reduce((sum, item) => sum + item.count, 0)
    const noReasonCount = churnData.find(item => item.reason === 'No reason provided')?.count ?? 0
    const reasonsGiven = churnData.filter(item => item.reason !== 'No reason provided')
    const reasonsGivenTotal = reasonsGiven.reduce((sum, item) => sum + item.count, 0)
    const noReasonPct = totalCancels > 0 ? Math.round((noReasonCount / totalCancels) * 100) : 0

    if (isLoading) {
        return <RevenueSkeleton />
    }

    return (
        <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                    Revenue
                </h1>
                <DashboardDateRangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    tooltipMessage="Filters trial conversion, churn rate, cancellation reasons, and MRR trend chart to the selected period. Snapshot metrics like MRR, ARR, ARPU, and LTV always reflect current state."
                />
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-3 gap-4">
                {statsCards.map((stat, index) => (
                    <Card
                        key={stat.title}
                        className="animate-in fade-in duration-300 py-0"
                        style={{ animationDelay: `${index * 50}ms` }}
                    >
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-lg shrink-0 ${stat.iconBg} ${stat.iconColor}`}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-2xl font-bold text-foreground leading-tight">
                                        {stat.value}
                                    </span>
                                    <span className="text-sm text-muted-foreground truncate flex items-center gap-1.5">
                                        {stat.title}
                                        {stat.tooltip && (
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors cursor-help" />
                                                    </TooltipTrigger>
                                                    <TooltipContent className="font-normal">
                                                        {stat.tooltip}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Plan Distribution */}
            <PlanDistributionCard items={planDistribution} />

            {/* MRR Trend */}
            <Card className="py-0 gap-0">
                <CardHeader className="py-6 gap-0 border-b">
                    <CardTitle className="text-lg font-semibold">MRR Over Time</CardTitle>
                    <CardDescription>Daily revenue snapshot</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 pb-6">
                    {mrrTrend.length > 0 ? (
                        <ChartContainer config={mrrChartConfig} className="h-[280px] w-full">
                            <AreaChart data={mrrTrend} accessibilityLayer margin={{ left: 0, right: 12, top: 12, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--color-mrr)" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="var(--color-mrr)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tickMargin={8} />
                                <YAxis axisLine={false} tickLine={false} tickMargin={8} width={50} tickFormatter={(v) => `$${v}`} />
                                <ChartTooltip
                                    content={
                                        <ChartTooltipContent
                                            labelFormatter={(_label, payload) => payload?.[0]?.payload?.fullDate || _label}
                                            formatter={(value, _name, item: any) => (
                                                <>
                                                    <div className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: item?.color || 'var(--color-mrr)' }} />
                                                    <div className="flex flex-1 justify-between leading-none items-center gap-4">
                                                        <span className="text-muted-foreground">MRR</span>
                                                        <span className="text-foreground font-mono font-medium tabular-nums">
                                                            ${Number(value).toLocaleString()}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        />
                                    }
                                />
                                <Area type="monotone" dataKey="mrr" stroke="var(--color-mrr)" strokeWidth={2} fill="url(#mrrGradient)" />
                            </AreaChart>
                        </ChartContainer>
                    ) : (
                        <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                            No MRR data available for this period
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Churn & Retention Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cancellation Reasons */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">Cancellation Reasons</CardTitle>
                            {stats && (
                                <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                                    {stats.downgradeOffersClaimed} offer claims
                                </Badge>
                            )}
                        </div>
                        <CardDescription>Why agencies cancel their subscriptions</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {churnData.length > 0 ? (
                            <div className="space-y-4">
                                {/* Coverage: most cancellations leave no reason, so surface that first */}
                                <div className="flex items-stretch gap-3">
                                    <div className="flex-1 p-3 rounded-lg bg-muted/50 text-center">
                                        <div className="text-2xl font-bold text-foreground leading-tight">{totalCancels}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">Total cancellations</div>
                                    </div>
                                    <div className="flex-1 p-3 rounded-lg bg-muted/50 text-center">
                                        <div className="text-2xl font-bold text-foreground leading-tight">
                                            {noReasonCount}
                                            <span className="text-base font-medium text-muted-foreground"> ({noReasonPct}%)</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">No reason given</div>
                                    </div>
                                </div>

                                {/* Ranked reasons (already sorted by count from the API) */}
                                {reasonsGiven.length > 0 ? (
                                    <div>
                                        <div className="text-xs font-medium text-muted-foreground mb-2">
                                            Reasons given by {reasonsGivenTotal} {reasonsGivenTotal === 1 ? 'agency' : 'agencies'}
                                        </div>
                                        <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                                            {reasonsGiven.map((item, idx) => {
                                                const pct = reasonsGivenTotal > 0 ? Math.round((item.count / reasonsGivenTotal) * 100) : 0
                                                return (
                                                    <div key={idx} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-muted/50">
                                                        <div className="min-w-0 flex-1">
                                                            <TruncatedReason text={item.reason} />
                                                        </div>
                                                        <div className="flex items-center gap-3 text-sm text-muted-foreground shrink-0">
                                                            <span className="font-medium text-foreground">{item.count}</span>
                                                            {item.count > 1 && <span>({pct}%)</span>}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                        No agencies gave a reason in this period.
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                                <div className="text-center">
                                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                        <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-medium mb-2">No cancellations</h3>
                                    <p className="text-sm text-muted-foreground">No cancellations recorded in this period.</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* At-Risk & Trial Expiry */}
                <Card className="py-0 gap-0">
                    <CardHeader className="py-6 gap-0 border-b">
                        <CardTitle className="text-lg font-semibold">Trial Expiry Timeline</CardTitle>
                        <CardDescription>Upcoming trial expirations</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 pb-6">
                        {trialExpiry.length > 0 ? (
                            <div className="space-y-2 max-h-[280px] overflow-y-auto">
                                {trialExpiry.map((agency) => (
                                    <Link
                                        key={agency.id}
                                        to={`/agencies/${agency.id}`}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-transparent hover:border-border hover:bg-muted transition-colors"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-sm text-foreground truncate">
                                                {agency.name}
                                            </span>
                                            <span className="text-xs text-muted-foreground mt-0.5">
                                                {agency.plan}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Badge
                                                variant="secondary"
                                                className={`font-medium text-xs shadow-none cursor-default ${
                                                    agency.daysRemaining <= 3
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-transparent'
                                                        : agency.daysRemaining <= 7
                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-transparent'
                                                        : ''
                                                }`}
                                            >
                                                {agency.daysRemaining <= 0 ? 'Expired' : `${agency.daysRemaining}d left`}
                                            </Badge>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="h-[280px] flex items-center justify-center">
                                <div className="text-center">
                                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                                        <Clock className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-medium mb-2">No active trials</h3>
                                    <p className="text-sm text-muted-foreground">No agencies are currently in trial.</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>


        </div>
    )
}

export default Revenue
